import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import ImageCropSavePage from './components/ImageSave';
import ImageSearchSelectMember from './components/ImageSearchSelectMember';
import type { ImageSearchResultType } from './types/type';
import ImageSearchResultContainer from './components/ImageSearchResult/ImageSearchResultContainer';
import { t } from 'i18next';
import { setEvent } from '../../utils/ga';

const STEPS = {
  SAVE_IMAGE: 'SAVE_IMAGE',
  SELECT_MEMBER: 'SELECT_MEMBER',
  SEARCH_RESULT: 'SEARCH_RESULT'
};

const ImageSearchPage = () => {
  const [step, setStep] = useState(STEPS.SAVE_IMAGE);
  const [imageData, setImageData] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<ImageSearchResultType['selectedMember']>({
    artistId: '',
    artistName: '',
    memberId: '',
    memberName: ''
  });
  const [searchParams] = useSearchParams();
  const origin = searchParams.get('origin');
  const originForSelectMethod = origin === 'select-method';

  // GA 이벤트 전송 함수
  const sendGAEvent = useCallback((action: string, label?: string, value?: number) => {
    setEvent({
      category: 'Image Search',
      action: action,
      label: label,
      value: value
    });
  }, []);

  // step 변경 시 GA 이벤트 전송
  useEffect(() => {
    switch (step) {
      case STEPS.SAVE_IMAGE:
        sendGAEvent('view_image_upload', '이미지 업로드 화면 진입');
        break;
      case STEPS.SELECT_MEMBER:
        sendGAEvent('view_member_selection', '멤버 선택 화면 진입');
        break;
      case STEPS.SEARCH_RESULT:
        sendGAEvent('view_search_result', '검색 결과 화면 진입');
        break;
    }
  }, [step, sendGAEvent]);

  const beforeUploadContent = {
    imageSrc: originForSelectMethod
      ? '/assets/icons/image-search/image_search.png'
      : '/assets/icons/image-search/image_save_content.png',
    title: originForSelectMethod
      ? t('imageSearch.beforeUpload.titleForSelectMethod')
      : t('imageSearch.beforeUpload.title'),
    description: originForSelectMethod
      ? t('imageSearch.beforeUpload.descriptionForSelectMethod')
      : t('imageSearch.beforeUpload.description'),
    disclaimerRowLine: [
      t('imageSearch.beforeUpload.disclaimerRowLine1'),
      t('imageSearch.beforeUpload.disclaimerRowLine2'),
      t('imageSearch.beforeUpload.disclaimerRowLine3')
    ],
    bottomDescription: t('imageSearch.beforeUpload.bottomDescription')
  };

  const onMemberSelect = ({
    artistId,
    artistName,
    memberId,
    memberName
  }: {
    artistId: string;
    artistName: string;
    memberId: string;
    memberName: string;
  }) => {
    setSelectedMember({ artistId, artistName, memberId, memberName });
    // 멤버 선택 완료 이벤트
    sendGAEvent('select_member', `${artistName} - ${memberName}`, 1);
    setStep(STEPS.SEARCH_RESULT);
  };

  return (
    <div>
      {step === STEPS.SAVE_IMAGE && (
        <ImageCropSavePage
          setImageData={setImageData}
          onComplete={() => {
            // 이미지 업로드 완료 이벤트
            sendGAEvent('complete_image_upload', '이미지 업로드 완료', 1);
            setStep(STEPS.SELECT_MEMBER);
          }}
          beforeUploadContent={beforeUploadContent}
        />
      )}
      {step === STEPS.SELECT_MEMBER && (
        <ImageSearchSelectMember
          onClose={() => {
            // 멤버 선택 화면에서 뒤로가기 이벤트
            sendGAEvent('back_from_member_selection', '멤버 선택 화면에서 뒤로가기', 1);
            setStep(STEPS.SAVE_IMAGE);
          }}
          onMemberSelect={onMemberSelect}
        />
      )}
      {step === STEPS.SEARCH_RESULT && imageData && (
        <ImageSearchResultContainer
          imageData={imageData}
          selectedMember={selectedMember}
          onBackClick={() => {
            // 검색 결과 화면에서 뒤로가기 이벤트
            sendGAEvent('back_from_search_result', '검색 결과 화면에서 뒤로가기', 1);
            setStep(STEPS.SELECT_MEMBER);
          }}
          onAnotherSearchClick={() => {
            // 다른 검색하기 클릭 이벤트
            sendGAEvent('failed_another_search', '실패 시 다른 검색하기 클릭', 1);
            setStep(STEPS.SAVE_IMAGE);
            setImageData(null);
          }}
        />
      )}
    </div>
  );
};

export default ImageSearchPage;
